import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, FloatingLabel } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import './DateRangePicker.css';
import styles from '../ManagementPages.module.css'
import { useSelector } from 'react-redux';
import { formatDateString } from '../../utils/dateUtils';

const DateRangePicker = ({onSave, onClear, mode = 'input', showModal, onClose, timeSelect = false}) => {
  const [show, setShow] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [inputValue, setInputValue] = useState('');
  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date(new Date().setMonth(new Date().getMonth() + 1)));
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const userDateFormat = useSelector((state) => state.userInfo.dateFormat);

  const inputRef = useRef(null);

  useEffect(() => {
    // Update input value when date format changes
    if (startDate && endDate) {
      setInputValue(`${formatDateString(startDate.toISOString(), userDateFormat)}${timeSelect ? ` ${startTime}` : ''} - ${formatDateString(endDate.toISOString(), userDateFormat)}${timeSelect ? ` ${endTime}` : ''}`);
    } else if (startDate) {
      setInputValue(`Start: ${formatDateString(startDate.toISOString(), userDateFormat)}${timeSelect ? ` ${startTime}` : ''}`);
    } else if (endDate) {
      setInputValue(`End: ${formatDateString(endDate.toISOString(), userDateFormat)}${timeSelect ? ` ${endTime}` : ''}`);
    }
  }, [userDateFormat, startDate, endDate, startTime, endTime]);

  const handleShow = () => {
    const inputRect = inputRef.current.getBoundingClientRect();
    setModalPosition({
      top: inputRect.bottom,
      left: inputRect.left + window.scrollX,
    });
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    if (onClose) onClose();
  };

  const handleSave = () => {
    const normalizeDate = (date, time, isEnd = false) => {
      if (!date) return null;
      const normalizedDate = new Date(date);
      const [hours, minutes] = time.split(':').map(Number);
      normalizedDate.setHours(hours, minutes, isEnd ? 59 : 0, isEnd ? 999 : 0);
      return normalizedDate
    };

    const normalizedStartDate = normalizeDate(startDate, startTime);
    const normalizedEndDate = normalizeDate(endDate, endTime, true);
    if (startDate && endDate) {
      setInputValue(`${formatDateString(startDate.toISOString(), userDateFormat)}${timeSelect ? ` ${startTime}` : ''} - ${formatDateString(endDate.toISOString(), userDateFormat)}${timeSelect ? ` ${endTime}` : ''}`);
    } else if (startDate) {
      setInputValue(`Start: ${formatDateString(startDate.toISOString(), userDateFormat)}${timeSelect ? ` ${startTime}` : ''}`);
    } else if (endDate) {
      setInputValue(`End: ${formatDateString(endDate.toISOString(), userDateFormat)}${timeSelect ? ` ${endTime}` : ''}`);
    }
    onSave(normalizedStartDate, normalizedEndDate, normalizedStartDate?.getTime(), normalizedEndDate?.getTime());
    handleClose();
  };

  const handleMonthChange = (direction) => {
    const newStartMonth = new Date(startMonth);
    const newEndMonth = new Date(endMonth);

    newStartMonth.setMonth(newStartMonth.getMonth() + direction);
    newEndMonth.setMonth(newEndMonth.getMonth() + direction);

    setStartMonth(newStartMonth);
    setEndMonth(newEndMonth);
  };

  const handleClearStartDate = () => {
    setStartDate(null);
  };

  const handleClearEndDate = () => {
    setEndDate(null);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setStartTime('00:00');
    setEndTime('23:59');
    setInputValue('');
    onClear();
  }

  const onChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div style={{ position: 'relative', paddingLeft: '10px' }}>
      {mode === 'input' && (
        <Form.Control
          type="text"
          style={{ width: '15vw', height: '44px', position: 'relative' }}
          placeholder="Date"
          value={inputValue}
          onClick={handleShow}
          readOnly
          ref={inputRef}
        />
      )}
      {mode === 'input' && (
        <FontAwesomeIcon
          icon={faTimesCircle}
          style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
          onClick={handleClear}
        />
      )}

      {(mode === 'modal' || show) && (
        <Modal
          show={mode === 'modal' ? showModal : show}
          onHide={handleClose}
          size='lg' 
          backdrop={mode === 'modal'}
          style={mode === 'modal' ? {} :{
            maxWidth: '530px',
            width: '530px',
            top: modalPosition.top,
            left: modalPosition.left,
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Select Date Range</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: 'flex', gap: '10px', position: 'relative', paddingLeft: '10px' }}>
              <FloatingLabel controlId="floatingStartDate" label="Start Date" className="mb-3">
                <Form.Control
                  type="text"
                  style={{width: "150px"}}
                  value={startDate ? formatDateString(startDate.toISOString(), userDateFormat) : ''}
                  readOnly
                />
                {startDate && (
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    onClick={handleClearStartDate}
                  />
                )}
              </FloatingLabel>
              {timeSelect &&
                <FloatingLabel controlId="floatingStartTime" label="Start Time" className="mb-3">
                  <Form.Control
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </FloatingLabel>
              }
              <span style={{ display: 'inline-block', width: '40px', textAlign: 'center' }}>to</span>
              <FloatingLabel controlId="floatingEndDate" label="End Date" className="mb-3">
                <Form.Control
                  type="text"
                  style={{width: "150px"}}
                  value={endDate ? formatDateString(endDate.toISOString(), userDateFormat) : ''}
                  readOnly
                />
                {endDate && (
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    onClick={handleClearEndDate}
                  />
                )}
              </FloatingLabel>
              {timeSelect &&
                <FloatingLabel controlId="floatingEndTime" label="End Time" className="mb-3">
                  <Form.Control
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </FloatingLabel>
              }
            </div>
            <div className="d-flex justify-content-left align-items-center">
              <DatePicker
                swapRange
                selectsRange
                selected={startDate}
                onChange={onChange}
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                isClearable={true}
                monthsShown={2}
                inline
                calendarStartDay={1}
                renderCustomHeader={({
                  monthDate,
                  customHeaderCount
                }) => (
                  <div className="custom-header">
                    <button
                      aria-label="Previous Month"
                      className="react-datepicker__navigation react-datepicker__navigation--previous"
                      style={customHeaderCount === 1 ? { visibility: 'hidden' } : null}
                      onClick={() => handleMonthChange(-1)}
                    >
                      <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
                        {'<'}
                      </span>
                    </button>
                    <span className="react-datepicker__current-month">
                      {monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      aria-label="Next Month"
                      className="react-datepicker__navigation react-datepicker__navigation--next"
                      style={customHeaderCount === 0 ? { visibility: 'hidden' } : null}
                      onClick={() => handleMonthChange(1)}
                    >
                      <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
                        {'>'}
                      </span>
                    </button>
                  </div>
                )}
                openToDate={startMonth}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button className={styles.CancelButton} variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button className={styles.CreateButton} variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default DateRangePicker;
